'use client'

interface NavbarComponentProps {
  theme: any;
  logoUrl: string;
  radius: number;
  fontFamily: string;
  availableSections?: string[];
}

export default function NavbarComponent({ 
  theme, 
  logoUrl, 
  radius, 
  fontFamily,
  availableSections = []
}: NavbarComponentProps) {
  
  // Generate navigation items based on available sections only (no duplicates)
  const allNavItems = availableSections.map(section => {
    const sectionLabels: Record<string, string> = {
      'audience': 'Our Audience',
      'inventory': 'Advertise',
      'contactfaq': 'Contact',
      'aboutus': 'About'
    };
    
    return {
      id: section,
      label: sectionLabels[section] || section,
      href: `#${section}`
    };
  }).filter(item => item.label !== item.id); // Only include known sections

  // Handle smooth scrolling with offset for fixed navbar
  const handleSmoothScroll = (href: string) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        const navbarHeight = 80; // Height of the navbar (h-20 = 80px)
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navbarHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      window.location.href = href;
    }
  };


  // Generate navbar background based on theme surface
  const getNavbarBackground = () => {
    return {
      background: `rgba(255, 255, 255, 0.7)`,
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      borderBottom: "1px solid rgba(0, 0, 0, 0.1)"
    };
  };

  return (
    <nav 
      className="w-full z-50 fixed top-0 left-0 right-0"
      style={{
        ...getNavbarBackground(),
        fontFamily
      }}
    >
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="WVON 1690 AM Logo" 
                className="h-12 w-auto"
                style={{ borderRadius: radius / 2 }}
              />
            ) : (
              <div 
                className="h-12 w-32 flex items-center justify-center text-lg font-bold font-primary"
                style={{ 
                  color: theme.text,
                  backgroundColor: theme.card,
                  borderRadius: radius / 2
                }}
              >
                Logo
              </div>
            )}
          </div>

          {/* Navigation + CTA */}
          <div className="flex items-center space-x-8">
            {/* Navigation Items */}
            <div className="hidden md:flex items-center space-x-6">
              {allNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSmoothScroll(item.href)}
                  className="text-sm font-medium transition-colors duration-200 hover:opacity-70 font-primary"
                  style={{ color: '#333333' }}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* CTA Button with Pulsating Indicator */}
            <div className="relative">
              {/* Pulsating Green Circle */}
              <div className="absolute -top-1 -right-1 w-3 h-3">
                <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></div>
                <div className="absolute inset-0 bg-green-500 rounded-full"></div>
              </div>
              
              <button
                onClick={() => (window as any).ChatWidget?.showChat()}
                className="px-4 py-2 text-sm font-semibold transition-all duration-200 hover:shadow-lg font-primary"
                style={{
                  backgroundColor: theme.accent,
                  color: theme.onButton,
                  borderRadius: radius
                }}
              >
                Assistant
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
